import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import {
  applyPwaUpdate,
  checkForPwaUpdate,
  dismissPwaUpdate,
  getPwaUpdateSnapshot,
  subscribePwaUpdate,
} from '../pwa';

export function PwaUpdateNotice(): JSX.Element | null {
  const available = useSyncExternalStore(
    subscribePwaUpdate,
    getPwaUpdateSnapshot,
    getPwaUpdateSnapshot,
  );
  const [dismissed, setDismissed] = useState(false);
  const dismissedRef = useRef(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleUpdateEvent = (): void => {
      dismissedRef.current = false;
      setDismissed(false);
      setError(null);
    };
    const handleVisibility = (): void => {
      if (document.visibilityState !== 'visible') return;
      void checkForPwaUpdate().then((found) => {
        if (found) {
          dismissedRef.current = false;
          setDismissed(false);
        }
      });
    };

    window.addEventListener('flower-blend:pwa-update-available', handleUpdateEvent);
    document.addEventListener('visibilitychange', handleVisibility);
    void checkForPwaUpdate().then((found) => {
      if (found && !dismissedRef.current) setDismissed(false);
    });
    return () => {
      window.removeEventListener('flower-blend:pwa-update-available', handleUpdateEvent);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  if (!available || dismissed) return null;

  const updateNow = async (): Promise<void> => {
    setApplying(true);
    setError(null);
    try {
      await applyPwaUpdate();
    } catch {
      setApplying(false);
      setError('The update could not be applied yet. Please try again later.');
    }
  };

  return (
    <aside className="pwa-update-notice" role="status" aria-live="polite">
      <div className="pwa-update-notice__copy">
        <strong>A Conscious Ritual update is ready.</strong>
        {error ? <span role="alert">{error}</span> : null}
      </div>
      <div className="pwa-update-notice__actions">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => {
            dismissedRef.current = true;
            dismissPwaUpdate();
            setDismissed(true);
          }}
          disabled={applying}
        >
          Later
        </button>
        <button type="button" className="btn btn-primary" onClick={updateNow} disabled={applying}>
          {applying ? 'Updating…' : 'Update Now'}
        </button>
      </div>
    </aside>
  );
}
